import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HeldLinkNotice } from "@/components/HeldLinkNotice";
import { TempPasswordDialog } from "@/components/TempPasswordDialog";
import { useViewAsClient } from "@/lib/viewAsClient";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  KeyRound,
  MoreHorizontal,
  Plus,
  Trash2,
  UserPlus,
  UserX,
  Users as UsersIcon,
} from "lucide-react";
import {
  addUserAssignment,
  deactivateUser,
  deleteUserAssignment,
  inviteUser,
  listClients,  listSitesLite,
  listUserDirectory,
  softDeleteUser,
  updateUserAssignment,
} from "@/lib/api";

const ROLES = [
  "super_admin",
  "client_admin",
  "site_admin",
  "dock_manager",
  "receiving",
  "dispatch",
  "gate",
  "broker",
  "driver",
];

// Roles ordered high → low; non-super admins may only grant client_admin and below (server enforces too).
function useGrantableRoles() {
  const { hasRole } = useAuth();
  return hasRole(["super_admin"]) ? ROLES : ROLES.slice(1);
}

export default function Users() {
  const { viewAsClientId } = useViewAsClient();
  const { hasRole, user: me } = useAuth();
  const isSuper = hasRole(["super_admin"]);
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(isSuper);   // default ON for super, OFF for client_admin
  const [confirmDelete, setConfirmDelete] = useState(null);  // user row pending delete
  const [tempPwUser, setTempPwUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [u, c, s] = await Promise.all([
        listUserDirectory(),
        listClients(),
        listSitesLite(),
      ]);
      if (cancelled) return;
      if (u.error) toast.error(u.error.message);
      let users = u.data || [];
      // View-as filter: show only users with at least one assignment in the active tenant.
      if (viewAsClientId) {
        users = users.filter((row) =>
          (row.assignments || []).some((a) => a.client_id === viewAsClientId)
        );
      }
      if (!showDeleted) users = users.filter((row) => !row.deleted_at);
      setRows(users);
      setClients(c.data || []);
      setSites(s.data || []);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId, tick, showDeleted]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { error } = await softDeleteUser(confirmDelete.user_id);
    setConfirmDelete(null);
    if (error) toast.error(error.message);
    else { toast.success("User deleted. Their bookings are preserved."); refresh(); }
  };

  return (
    <div data-testid="users-page" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <UsersIcon className="h-6 w-6" /> Users
          </h1>
          <p className="text-sm text-slate-500">
            Invite teammates and manage roles. Drivers + brokers see only their own bookings (RLS).
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-slate-500 select-none">
            <Switch
              checked={showDeleted}
              onCheckedChange={setShowDeleted}
              data-testid="users-show-deleted-toggle"
            />
            Show deleted
          </label>
          <Button onClick={() => setInviteOpen(true)} data-testid="users-invite-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Invite user
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center text-sm text-slate-400">
                  No users yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((u) => (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell>
                    <div className="font-medium">
                      {(u.first_name || u.last_name)
                        ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                        : u.email}
                    </div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </TableCell>
                  <TableCell>
                    <AssignmentList
                      user={u}
                      clients={clients}
                      sites={sites}
                      onChanged={refresh}
                    />
                  </TableCell>
                  <TableCell>
                    {u.deleted_at ? (
                      <Badge variant="secondary" className="bg-rose-100 text-rose-700" data-testid={`user-deleted-badge-${u.user_id}`}>
                        Deleted
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className={u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}
                      >
                        {u.is_active ? "Active" : "Inactive"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.deleted_at ? null : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`user-actions-${u.user_id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {u.user_id !== me?.id && (
                          <>
                            <DropdownMenuItem onClick={() => setTempPwUser(u)} data-testid={`user-temp-password-${u.user_id}`}>
                              <KeyRound className="mr-2 h-4 w-4" /> Set temporary password
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem
                          onClick={async () => {
                            const r = await deactivateUser(u.user_id);
                            if (r.error) toast.error(r.error.message);
                            else { toast.success("Deactivated."); refresh(); }
                          }}
                          className="text-rose-600 focus:text-rose-700"
                          data-testid={`user-deactivate-${u.user_id}`}
                        >
                          <UserX className="mr-2 h-4 w-4" /> Deactivate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setConfirmDelete(u)}
                          className="text-rose-700 focus:text-rose-800"
                          data-testid={`user-delete-${u.user_id}`}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete user…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              This soft-deletes <strong>{confirmDelete?.email}</strong>. They lose all
              tenant assignments and cannot log in. Their historical bookings stay
              intact for audit. This action cannot be undone from the UI.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={doDelete}
              data-testid="confirm-delete-submit"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TempPasswordDialog user={tempPwUser} onOpenChange={(v) => !v && setTempPwUser(null)} />

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        clients={clients}
        sites={sites}
        onInvited={refresh}
      />
    </div>
  );
}

/* ─── inline assignment chips + add-row ─────────────────────────────────── */
function AssignmentList({ user, clients, sites, onChanged }) {
  const grantable = useGrantableRoles();
  const [adding, setAdding] = useState(false);
  const [role, setRole] = useState("dock_manager");
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");

  const add = async () => {
    if (!role) return;
    // Guardrail (2026-07-03): every non-super_admin role MUST be scoped.
    // Prior to this, unscoped assignments (client_admin with client_id
    // NULL, etc.) silently granted zero access AND broke RLS policies
    // downstream — see security audit SEC-001. Enforce at UI layer.
    const err = validateScope(role, clientId, siteId);
    if (err) { toast.error(err); return; }
    const { error } = await addUserAssignment({
      user_id: user.user_id,
      role,
      client_id: clientId || null,
      site_id: siteId || null,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Role assigned.");
    setAdding(false);
    setClientId("");
    setSiteId("");
    onChanged?.();
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {(user.assignments || []).map((a) => (
          <span
            key={a.id}
            className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] bg-slate-50"
            data-testid={`user-assignment-${a.id}`}
          >
            <strong>{a.role.replace("_", " ")}</strong>
            {a.client_id && clientById(clients, a.client_id) ? ` · ${clientById(clients, a.client_id)}` : ""}
            {a.site_id && siteById(sites, a.site_id) ? ` · ${siteById(sites, a.site_id)}` : ""}
            <button
              type="button"
              className="ml-1 text-rose-500 hover:text-rose-700"
              onClick={async () => {
                const { error } = await deleteUserAssignment(a.id);
                if (error) toast.error(error.message);
                else { toast.success("Removed."); onChanged?.(); }
              }}
              data-testid={`user-assignment-remove-${a.id}`}
              title="Remove role"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
        {!adding && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 text-xs"
            onClick={() => setAdding(true)}
            data-testid={`user-add-role-${user.user_id}`}
          >
            <Plus className="h-3 w-3 mr-0.5" /> Add role
          </Button>
        )}
      </div>
      {adding && (
        <div className="flex flex-wrap gap-2 items-end pt-1">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`user-add-role-select-${user.user_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {grantable.map((r) => (
                <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`user-add-role-client-${user.user_id}`}><SelectValue placeholder="Client (optional)" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`user-add-role-site-${user.user_id}`}><SelectValue placeholder="Site (optional)" /></SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8 text-xs" onClick={add} data-testid={`user-add-role-submit-${user.user_id}`}>Add</Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setAdding(false)} data-testid={`user-add-role-cancel-${user.user_id}`}>Cancel</Button>
        </div>
      )}
    </div>
  );
}

function clientById(clients, id) {
  return clients.find((c) => c.id === id)?.name || "";
}
function siteById(sites, id) {
  return sites.find((s) => s.id === id)?.name || "";
}

/**
 * Guardrail — every role except super_admin must be scoped, or the
 * resulting `lbs_user_assignments` row grants zero access AND trips
 * RLS predicates that assume the scope columns are populated. This
 * caused the orphaned client_admin (client_id NULL) row cleaned up
 * by 097. Returns an error string, or "" when the tuple is valid.
 */
function validateScope(role, clientId, siteId) {
  if (role === "super_admin") return "";
  if (role === "client_admin" && !clientId) {
    return "client_admin needs a client selected — otherwise the assignment grants nothing.";
  }
  if (role === "site_admin" && !siteId) {
    return "site_admin needs a site selected.";
  }
  if ((role === "booker" || role === "driver") && !clientId) {
    return `${role.replace("_", " ")} needs a client selected.`;
  }
  if (role === "dock_manager" && !clientId && !siteId) {
    return "dock_manager needs at least a client (or a specific site).";
  }
  return "";
}

/* ─── invite dialog ─────────────────────────────────────────────────────── */
function InviteDialog({ open, onOpenChange, clients, sites, onInvited }) {
  const grantable = useGrantableRoles();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("dock_manager");
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [busy, setBusy] = useState(false);
  const [heldLink, setHeldLink] = useState(null);

  const submit = async () => {
    if (!email || !role) {
      toast.error("Email and role are required.");
      return;
    }
    const err = validateScope(role, clientId, siteId);
    if (err) { toast.error(err); return; }
    setBusy(true);
    const { data, error } = await inviteUser({
      email,
      role,
      client_id: clientId || null,
      site_id: siteId || null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data?.held) {
      setHeldLink(data.action_link || "");
      toast.warning("Email hold is on — no email sent");
      onInvited?.();
      return;
    }
    toast.success(`Invite sent to ${email}.`);
    setEmail("");
    setClientId("");
    setSiteId("");
    onInvited?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setHeldLink(null); onOpenChange(o); }}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite user</DialogTitle>
          <DialogDescription>
            A magic-link email will be sent via Supabase Auth (unless the tenant is on
            email hold — then a link is shown instead). The chosen role is
            pre-staged so the user gets access on first sign-in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Field label="Email *">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" data-testid="invite-email" />
          </Field>
          <Field label="Role *">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {grantable.map((r) => (
                  <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Client (optional)">
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="invite-client"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Site (optional)">
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger data-testid="invite-site"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {heldLink !== null && <HeldLinkNotice link={heldLink} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setHeldLink(null); onOpenChange(false); }}>
            {heldLink !== null ? "Close" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={busy || heldLink !== null} data-testid="invite-submit">
            {busy ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
